import { z } from 'zod';

/** File from backend: id, name, url (S3 or pre-signed). */
export const financialDocumentFile$ = z.object({
  id: z.number(),
  name: z.string().optional(),
  url: z.string().nullable(),
  original_url: z.string().nullable().optional()
});

/** Amount stored as integer (2-decimal as cents) in DB; convert to whole units for app. */
const amountFromCents$ = z
  .number()
  .optional()
  .transform(n => (typeof n === 'number' ? n / 100 : 0));

/** Raw invoice/receipt document from API (snake_case). */
export const invoiceOrReceipt$ = z.object({
  id: z.number(),
  entity_type: z.number(),
  entity_id: z.number(),
  document_data: z
    .object({
      amount: amountFromCents$,
      currency: z.string().optional(),
      reference: z.string().optional()
    })
    .nullable(),
  status: z.union([z.number(), z.string()]).optional().nullable(),
  file_id: z.number().nullable().optional(),
  /** Backend always returns nested file object (null when no attachment). */
  file: financialDocumentFile$.nullable()
});

/** Linked expense on a line item. */
export const reconciliationLineItemExpense$ = z.object({
  expenseId: z.number(),
  name: z.string().nullable().optional(),
  categoryName: z.string().nullable().optional(),
  vendor: z.string().nullable(),
  receiptIds: z.array(z.number())
});

/** Raw reconciliation line item from API. */
export const reconciliationLineItemRaw$ = z.object({
  id: z.number(),
  /** Unique per row; present for bookings with multiple assignments to avoid duplicate keys. */
  assignmentId: z.number().nullable().optional(),
  bookingId: z.number().nullable(),
  expenseId: z.number().nullable().optional(),
  productionBudgetExpenseId: z.number().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  currency: z.string().nullable(),
  entityType: z.union([z.number(), z.literal('booking'), z.literal('expense')]),
  production: z.object({
    id: z.number(),
    code: z.coerce.string().nullable().optional(),
    title: z.string().nullable().optional(),
    ownerId: z.string().nullable().optional(),
    color: z.string().nullable().optional()
  }),
  resource: z.object({
    id: z.number().nullable().optional(),
    resource_type_id: z.number().nullable().optional(),
    pronouns_option_id: z.number().nullable().optional(),
    role: z.string().nullable().optional(),
    vendor: z.string().nullable().optional(),
    organisation: z.string().nullable().optional(),
    booking_status_id: z.number().nullable().optional()
  }),
  budgeted: z.object({
    committed: amountFromCents$,
    actual: amountFromCents$
  }),
  invoiceOrReceipts: z.array(invoiceOrReceipt$).optional().default([]),
  expenses: z.array(reconciliationLineItemExpense$).optional().default([])
});

/** Reconciliation list API response. */
export const reconciliationListResponse$ = z.object({
  results: z.array(reconciliationLineItemRaw$),
  paging: z
    .object({
      total: z.coerce.number(),
      page: z.coerce.number(),
      count_per_page: z.coerce.number()
    })
    .optional()
});

export type InvoiceOrReceiptRaw = z.infer<typeof invoiceOrReceipt$>;
export type ReconciliationLineItemRaw = z.infer<
  typeof reconciliationLineItemRaw$
>;
export type ReconciliationListResponse = z.infer<
  typeof reconciliationListResponse$
>;
